import * as zExt from "@redotech/rpc/ext";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod/v4";

export enum PointsTransactionType {
  REDEMPTION = "REDEMPTION",
  LABEL_PURCHASED = "LABEL_PURCHASED",
  LABEL_VOIDED = "LABEL_VOIDED",
  REFUND = "REFUND", // This happens when we have to refund a balance transaction that earned points
}

export enum RedemptionMethod {
  OUTBOUND_LABELS_BALANCE_CREDIT = "OUTBOUND_LABELS_BALANCE_CREDIT",
}

export const RedemptionMethodToLabel: Record<RedemptionMethod, string> = {
  [RedemptionMethod.OUTBOUND_LABELS_BALANCE_CREDIT]: "Balance credit",
} as const;

export const PointsSchema = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  currentBalance: z.number().int(),
  createdAt: z.date(),
  updatedAt: z.date(),
});

export type Points = z.infer<typeof PointsSchema>;

const BasePointsTransactionSchema = z.object({
  _id: zExt.objectId(),
  team: zExt.objectId(),
  transactionTime: z.date(),
  pointsChange: z.number().int(),
});

export const LabelPurchasedPointsTransactionSchema =
  BasePointsTransactionSchema.extend({
    transactionType: z.literal(PointsTransactionType.LABEL_PURCHASED),
    fulfillmentGroupId: zExt.objectId(),
  });

export type LabelPurchasedPointsTransaction = z.infer<
  typeof LabelPurchasedPointsTransactionSchema
>;

const RedemptionPointsTransactionSchema = BasePointsTransactionSchema.extend({
  transactionType: z.literal(PointsTransactionType.REDEMPTION),
  redemptionMethod: createEnum(RedemptionMethod, {
    enumName: "RedemptionMethod",
  }),
});

const LabelVoidedPointsTransactionSchema = BasePointsTransactionSchema.extend({
  transactionType: z.literal(PointsTransactionType.LABEL_VOIDED),
  fulfillmentGroupId: zExt.objectId(),
  labelPurchasedPointsTransactionId: zExt.objectId(),
});

const RefundPointsTransactionSchema = BasePointsTransactionSchema.extend({
  transactionType: z.literal(PointsTransactionType.REFUND),
  fulfillmentGroupId: zExt.objectId(),
  balanceTransactionRefundFor: zExt.objectId(),
});

export const PointsTransactionSchema = z.discriminatedUnion("transactionType", [
  LabelPurchasedPointsTransactionSchema,
  RedemptionPointsTransactionSchema,
  LabelVoidedPointsTransactionSchema,
  RefundPointsTransactionSchema,
]);

export type PointsTransaction = z.infer<typeof PointsTransactionSchema>;
